import {
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonToolbar,
  type RefresherEventDetail,
  useIonViewWillEnter,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  calendarOutline,
  checkmarkCircleOutline,
  helpCircleOutline,
  mailOutline,
} from "ionicons/icons";
import { Content } from "@/components/content";
import { UpcomingApplicationCardOverview } from "@/components/molecules/application/upcoming-application.card.overview";
import { EmptyState } from "@/components/molecules/empty-state";
import { InfoModal } from "@/components/molecules/info-modal";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { CompletedShiftCardOverview } from "@/components/molecules/shift/completed-shift-card.overview";
import { UpcomingShiftCardOverview } from "@/components/molecules/shift/upcoming-shift-card.overview";
import { Toolbar } from "@/components/toolbar";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { useSeparatedShiftsAndApplications } from "@/lib/hooks/use-separated-shifts-and-applications";
import { useUpcomingShiftsOverview } from "@/lib/hooks/use-upcoming-shifts-overview";
import { useMyCompletedShifts } from "@/lib/query/shift/use-my-completed-shifts";

/**
 * Example of how to add a settings button in the header:
 *
 * @example
 *   <IonButtons collapse slot="primary">
 *     <Button
 *       routerLink={Routes.Settings()}
 *       routerDirection="forward"
 *       shape="round"
 *       color="light"
 *       size="small"
 *       className="touch-target min-h-0 part-icon:m-0 part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:bg-surface-secondary part-native:p-1 part-native:text-xs part-native:text-txt-primary"
 *     >
 *       <IonIcon slot="icon-only" icon={cog6ToothIcon} className="size-5" />
 *     </Button>
 *   </IonButtons>;
 */
export function UpcomingShiftsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: featureFlags } = useFeatureFlags();
  const separationEnabled = featureFlags?.separationBetweenShiftsAndApplicationsInApp ?? false;
  const infoModalRef = useRef<HTMLIonModalElement>(null);

  const [selectedTab, setSelectedTab] = useState<
    "upcoming" | "shifts" | "applications" | "completed"
  >("upcoming");

  // Dynamic header title and info content based on selected tab
  const getHeaderTitle = () => {
    if (!separationEnabled) {
      return t("routes:upcoming-shifts.title");
    }
    switch (selectedTab) {
      case "applications":
        return t("routes:upcoming-shifts.applications-tab");
      case "shifts":
        return t("routes:upcoming-shifts.shifts-tab");
      case "completed":
        return t("routes:upcoming-shifts.completed-tab");
      default:
        return t("routes:upcoming-shifts.title");
    }
  };

  // Info modal content based on selected tab
  const getInfoSections = () => {
    switch (selectedTab) {
      case "applications":
        return [
          {
            content: t("routes:upcoming-shifts.info.applications.description"),
          },
          {
            title: t("routes:upcoming-shifts.info.applications.section1-title"),
            content: t("routes:upcoming-shifts.info.applications.section1-content"),
          },
          {
            title: t("routes:upcoming-shifts.info.applications.section2-title"),
            content: t("routes:upcoming-shifts.info.applications.section2-content"),
          },
        ];
      case "shifts":
        return [
          {
            content: t("routes:upcoming-shifts.info.shifts.description"),
          },
          {
            title: t("routes:upcoming-shifts.info.shifts.section1-title"),
            content: t("routes:upcoming-shifts.info.shifts.section1-content"),
          },
        ];
      case "completed":
        return [
          {
            content: t("routes:upcoming-shifts.info.completed.description"),
          },
          {
            title: t("routes:upcoming-shifts.info.completed.section1-title"),
            content: t("routes:upcoming-shifts.info.completed.section1-content"),
          },
        ];
      default:
        return [
          {
            content: t("routes:upcoming-shifts.info.upcoming.description"),
          },
        ];
    }
  };

  // Always call both hooks (React rules), but we'll only use data from the active one
  // The unused hook's queries will be cached but not actively used in rendering
  const merged = useUpcomingShiftsOverview();
  const separated = useSeparatedShiftsAndApplications(featureFlags ?? undefined);

  // Sync tab state when feature flag loads or changes
  useEffect(() => {
    if (separationEnabled && selectedTab === "upcoming") {
      // Flag turned ON and we're on the old "upcoming" tab -> switch to "applications" (Aangemeld)
      setSelectedTab("applications");
    } else if (!separationEnabled && (selectedTab === "shifts" || selectedTab === "applications")) {
      // Flag turned OFF and we're on a separated tab -> switch to "upcoming"
      setSelectedTab("upcoming");
    }
  }, [separationEnabled, selectedTab]);

  const {
    shifts: completedShifts,
    loadMore: loadMoreCompleted,
    hasNextPage: hasNextPageCompleted,
    refetch: refetchCompleted,
  } = useMyCompletedShifts();

  // Count completed shifts that need hours submission
  const pendingHoursSubmissionCount = completedShifts.filter(
    (shift) =>
      !shift.shiftTimeEntry?.id && // No shift time entry ID means hours not submitted
      shift.project?.allowEmployeeSubmitHours === true, // Project allows employee to submit hours
  ).length;

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    // Refetch all queries for all tabs:
    // - "upcoming" tab: merged shifts and applications
    // - "shifts" tab: separated shifts
    // - "applications" tab: separated applications
    // - "completed" tab: completed shifts
    await Promise.all([
      // Refetch merged view (upcoming tab when separationEnabled is false)
      merged.refetch(),
      // Refetch separated view (shifts and applications tabs when separationEnabled is true)
      separated.refetch(),
      // Refetch completed shifts (completed tab)
      refetchCompleted(),
      // Also refetch individual queries to ensure everything is up to date
      queryClient.refetchQueries({ queryKey: ["shifts"] }),
      queryClient.refetchQueries({ queryKey: ["shift"] }),
      queryClient.refetchQueries({ queryKey: ["applications"] }),
      queryClient.refetchQueries({ queryKey: ["expense-claim-file"] }),
    ]);

    event.detail.complete();
  };

  // Refetch all data when navigating back to this page
  useIonViewWillEnter(() => {
    void Promise.all([
      // Refetch merged view (upcoming tab when separationEnabled is false)
      merged.refetch(),
      // Refetch separated view (shifts and applications tabs when separationEnabled is true)
      separated.refetch(),
      // Refetch completed shifts (completed tab)
      refetchCompleted(),
      // Also refetch individual queries to ensure everything is up to date
      queryClient.refetchQueries({ queryKey: ["shifts"] }),
      queryClient.refetchQueries({ queryKey: ["shift"] }),
      queryClient.refetchQueries({ queryKey: ["applications"] }),
      queryClient.refetchQueries({ queryKey: ["expense-claim-file"] }),
    ]);
  });

  // Unified infinite scroll handler
  const handleInfiniteScroll = (ev: CustomEvent) => {
    if (selectedTab === "completed") {
      return loadMoreCompleted(ev);
    } else if (selectedTab === "shifts" && separationEnabled) {
      return separated.loadMoreShifts(ev);
    } else if (selectedTab === "applications" && separationEnabled) {
      return separated.loadMoreApplications(ev);
    } else if (selectedTab === "upcoming" && !separationEnabled) {
      return merged.loadMore(ev);
    }
    // Fallback: complete the event to prevent infinite scroll from hanging
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  // Determine if infinite scroll should be disabled
  const isInfiniteScrollDisabled = (() => {
    if (selectedTab === "completed") {
      return !hasNextPageCompleted;
    }
    if (selectedTab === "shifts" && separationEnabled) {
      return !separated.hasNextPageShifts;
    }
    if (selectedTab === "applications" && separationEnabled) {
      return !separated.hasNextPageApplications;
    }
    if (selectedTab === "upcoming" && !separationEnabled) {
      return !merged.hasNextPage;
    }
    return true; // Default to disabled if no match
  })();

  return (
    <IonPage>
      <IonHeader>
        <Toolbar className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          <div className="relative flex items-center gap-1 py-0">
            <h1 className="text-2xl font-semibold leading-[1.2] text-txt-primary">
              {getHeaderTitle()}
            </h1>
            {separationEnabled && (
              <button
                type="button"
                onClick={() => infoModalRef.current?.present()}
                aria-label={t("routes:upcoming-shifts.info-label")}
                className="ml-auto flex size-12 shrink-0 items-center justify-center rounded-lg p-3 transition-opacity active:opacity-70"
              >
                <IonIcon icon={helpCircleOutline} className="size-6 text-txt-tertiary" />
              </button>
            )}
            <OrganizationDropdown />
          </div>
        </Toolbar>
      </IonHeader>
      <Content scrollUnderChrome>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        {/* Segmented control */}
        <IonToolbar className="ion-no-padding border-b border-solid border-bordercolor-primary bg-surface-primary">
          <div className="flex w-full items-center justify-between gap-2">
            {!separationEnabled ? (
              // 3-tab layout (current behavior - flag OFF)
              <>
                <button
                  type="button"
                  onClick={() => setSelectedTab("upcoming")}
                  className={`relative flex flex-1 flex-col items-center p-0 after:absolute after:bottom-0 after:h-1 after:w-full after:rounded-[32px] ${
                    selectedTab === "upcoming" ? "after:bg-txt-primary" : "after:bg-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 px-0 py-2">
                    <span
                      className={`text-md font-medium leading-[1.5] ${
                        selectedTab === "upcoming" ? "text-txt-primary" : "text-txt-secondary"
                      }`}
                    >
                      {t("routes:upcoming-shifts.upcoming-tab")}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTab("completed")}
                  className={`relative flex flex-1 flex-col items-center p-0 after:absolute after:bottom-0 after:h-1 after:w-full after:rounded-[32px] ${
                    selectedTab === "completed" ? "after:bg-txt-primary" : "after:bg-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 px-0 py-2">
                    <span
                      className={`text-md font-medium leading-[1.5] ${
                        selectedTab === "completed" ? "text-txt-primary" : "text-txt-secondary"
                      }`}
                    >
                      {t("routes:upcoming-shifts.completed-tab")}
                    </span>
                    {pendingHoursSubmissionCount > 0 && (
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-txt-primary">
                        <span className="text-xs font-semibold leading-none text-txt-inverted">
                          {pendingHoursSubmissionCount}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              </>
            ) : (
              // 4-tab layout (new behavior - flag ON) - Order: Aangemeld → Bevestigd → Afgerond → Uitgenodigd
              <>
                <button
                  type="button"
                  onClick={() => setSelectedTab("applications")}
                  className={`relative flex flex-1 flex-col items-center p-0 after:absolute after:bottom-0 after:h-1 after:w-full after:rounded-[32px] ${
                    selectedTab === "applications" ? "after:bg-txt-primary" : "after:bg-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 px-0 py-2">
                    <span
                      className={`text-sm font-medium leading-[1.4] ${
                        selectedTab === "applications" ? "text-txt-primary" : "text-txt-secondary"
                      }`}
                    >
                      {t("routes:upcoming-shifts.applications-tab")}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTab("shifts")}
                  className={`relative flex flex-1 flex-col items-center p-0 after:absolute after:bottom-0 after:h-1 after:w-full after:rounded-[32px] ${
                    selectedTab === "shifts" ? "after:bg-txt-primary" : "after:bg-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 px-0 py-2">
                    <span
                      className={`text-sm font-medium leading-[1.4] ${
                        selectedTab === "shifts" ? "text-txt-primary" : "text-txt-secondary"
                      }`}
                    >
                      {t("routes:upcoming-shifts.shifts-tab")}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTab("completed")}
                  className={`relative flex flex-1 flex-col items-center p-0 after:absolute after:bottom-0 after:h-1 after:w-full after:rounded-[32px] ${
                    selectedTab === "completed" ? "after:bg-txt-primary" : "after:bg-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 px-0 py-2">
                    <span
                      className={`text-sm font-medium leading-[1.4] ${
                        selectedTab === "completed" ? "text-txt-primary" : "text-txt-secondary"
                      }`}
                    >
                      {t("routes:upcoming-shifts.completed-tab")}
                    </span>
                    {pendingHoursSubmissionCount > 0 && (
                      <div className="flex h-4 w-4 items-center justify-center rounded-full bg-txt-primary">
                        <span className="text-2xs font-semibold leading-none text-txt-inverted">
                          {pendingHoursSubmissionCount}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              </>
            )}
          </div>
        </IonToolbar>

        {selectedTab === "upcoming" && !separationEnabled && merged.items.length === 0 && (
          <EmptyState
            icon={calendarOutline}
            title={t("routes:upcoming-shifts.empty-upcoming-title")}
            description={t("routes:upcoming-shifts.empty-upcoming-description")}
          />
        )}
        {selectedTab === "shifts" && separationEnabled && separated.shifts.length === 0 && (
          <EmptyState
            icon={calendarOutline}
            title={t("routes:upcoming-shifts.empty-shifts")}
            description={t("routes:upcoming-shifts.empty-shifts-description")}
          />
        )}
        {selectedTab === "applications" &&
          separationEnabled &&
          separated.applications.length === 0 && (
            <EmptyState
              icon={calendarOutline}
              title={t("routes:upcoming-shifts.empty-applications")}
              description={t("routes:upcoming-shifts.empty-applications-description")}
            />
          )}
        {selectedTab === "completed" && completedShifts.length === 0 && (
          <EmptyState
            icon={checkmarkCircleOutline}
            title={t("routes:upcoming-shifts.empty-completed-title")}
            description={t("routes:upcoming-shifts.empty-completed-description")}
          />
        )}

        <IonList key={selectedTab} className="flex flex-col gap-5 px-6 py-8">
          {selectedTab === "upcoming" && !separationEnabled && merged.items.length > 0
            ? merged.items.map((item) => {
                if (item.type === "shift") {
                  return <UpcomingShiftCardOverview key={item.shift.id} shift={item.shift} />;
                } else {
                  return (
                    <UpcomingApplicationCardOverview
                      key={item.application.id}
                      application={item.application}
                    />
                  );
                }
              })
            : null}
          {selectedTab === "shifts" && separationEnabled && separated.shifts.length > 0
            ? separated.shifts.map((shift) => (
                <UpcomingShiftCardOverview key={shift.id} shift={shift} />
              ))
            : null}
          {selectedTab === "applications" && separationEnabled && separated.applications.length > 0
            ? separated.applications.map((application) => (
                <UpcomingApplicationCardOverview key={application.id} application={application} />
              ))
            : null}
          {selectedTab === "completed" && completedShifts.length > 0
            ? completedShifts.map((shift) => (
                <CompletedShiftCardOverview key={shift.id} shift={shift} />
              ))
            : null}
        </IonList>

        <IonInfiniteScroll onIonInfinite={handleInfiniteScroll} disabled={isInfiniteScrollDisabled}>
          <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </Content>

      {/* Info Modal */}
      <InfoModal modalRef={infoModalRef} title={getHeaderTitle()} sections={getInfoSections()} />
    </IonPage>
  );
}
